import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, StatusPill } from "@/components/ui-helpers";
import { ArrowRight } from "lucide-react";

export default function Funnel() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/funnel/flow").then((r) => setData(r.data)); }, []);

  if (!data) return <div className="p-6 text-xs text-neutral-500">Loading…</div>;

  const findNode = (id) => data.nodes.find((n) => n.id === id);

  const Node = ({ id, lane }) => {
    const n = findNode(id);
    if (!n) return null;
    const laneColor = lane === "qualified" ? "border-l-[var(--ok)]" : lane === "dq" ? "border-l-[var(--warn)]" : lane === "planning" ? "border-l-[var(--ai)]" : "";
    return (
      <div className={`panel p-3 w-44 shrink-0 border-l-2 ${laneColor}`} data-testid={`node-${id}`}>
        <div className="flex items-center justify-between gap-2">
          <div className="text-xs font-medium truncate">{n.label}</div>
          <StatusPill status={n.status} />
        </div>
        {typeof n.count === "number" && (
          <div className="text-xl font-mono font-medium mt-2">{n.count}</div>
        )}
        <div className="text-[10px] text-neutral-500">7d count</div>
      </div>
    );
  };

  const Arrow = () => <ArrowRight size={14} className="text-neutral-600 shrink-0" strokeWidth={1.5} />;

  return (
    <div data-testid="funnel-page">
      <PageHeader
        title="Funnel"
        sub="Meta → Typeform → Qualification gate → 3 lanes. 7d counts shown."
      />

      <div className="p-4 md:p-6 space-y-6">
        {/* Top row: ingest */}
        <section className="panel-2 p-4 overflow-x-auto">
          <div className="flex items-center gap-3 min-w-max">
            <Node id="meta" />
            <Arrow />
            <Node id="typeform" />
            <Arrow />
            <Node id="gate" />
          </div>
        </section>

        {/* Three lanes */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Qualified lane */}
          <div className="panel p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="dot dot-ok" />
              <h3 className="text-sm font-medium">Qualified (GMV ≥ bar, event ≤ 60d)</h3>
            </div>
            <div className="space-y-3">
              <Node id="qualified" lane="qualified" />
              <Arrow />
              <Node id="calendly" lane="qualified" />
              <Arrow />
              <Node id="zoho" lane="qualified" />
              <Arrow />
              <Node id="wati" lane="qualified" />
            </div>
          </div>
          {/* DQ lane */}
          <div className="panel p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="dot dot-warn" />
              <h3 className="text-sm font-medium">DQ (GMV &lt; bar)</h3>
            </div>
            <div className="space-y-3">
              <Node id="dq" lane="dq" />
              <div className="text-[11px] text-neutral-500 pl-1">→ DQ Leads → Sprint message → Framer offer → Razorpay (₹20k + GST)</div>
            </div>
          </div>
          {/* Planning lane */}
          <div className="panel p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="dot dot-ai" />
              <h3 className="text-sm font-medium">Planning (60+ days / Still planning)</h3>
            </div>
            <div className="space-y-3">
              <Node id="planning" lane="planning" />
              <Arrow />
              <Node id="zoho" lane="planning" />
              <Arrow />
              <Node id="mailerlite" lane="planning" />
            </div>
          </div>
        </section>

        {/* Delivery checks */}
        <section className="panel p-4" data-testid="delivery-checks">
          <h2 className="text-sm font-medium mb-3">Delivery checks</h2>
          <div className="space-y-2">
            {data.delivery_checks.map((d) => (
              <div key={d.hop} className="flex items-center justify-between text-xs panel-2 p-3">
                <div className="min-w-0">
                  <div className="font-mono text-neutral-300">{d.hop}</div>
                  <div className="text-[11px] text-neutral-500 mt-0.5">{d.note}</div>
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider text-neutral-500">In / Out</div>
                    <div className="font-mono">{d.in} / {d.out}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider text-neutral-500">Δ</div>
                    <div className="font-mono">{d.delta}</div>
                  </div>
                  <StatusPill status={d.status} />
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Conversion table */}
        <section className="panel p-4">
          <h2 className="text-sm font-medium mb-3">Conversion (7d)</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {[
              ["Typeform submits", data.conversion_7d.typeform_submits],
              ["Qualified", data.conversion_7d.qualified],
              ["Meet I", data.conversion_7d.meet_i],
              ["Won", data.conversion_7d.won],
            ].map(([l, v]) => (
              <div key={l} className="panel-2 p-3">
                <div className="text-[10px] uppercase tracking-wider text-neutral-500">{l}</div>
                <div className="text-lg font-mono mt-1">{v}</div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
